/**
 * What U.S. LawShield's promo system (the TLS promo API) said about a promo code.
 *
 * INFORMATIONAL ONLY (owner rule 2026-10-25). Some codes live only in USLS's own CRM/AP and not in the platform the
 * API checks, so a "not recognised" answer must NEVER block saving a CTA, generating its QR, making a flyer or issuing
 * a voucher. Nothing in this file is read by any of those paths -- it only feeds PromoCodeLookupStatus.
 *
 * Backed by migration 2026-10-25-995: cta_promo_code_status (read) and cta_promo_code_recheck (wraps the sanctioned
 * tls_promo_recheck, rate-limited to once a minute per code).
 */
import { supabase } from "./supabase";

export type PromoLookupState =
  | "live"
  | "not_found"
  | "error"
  | "pending"
  | "unchecked";

export interface PromoCodeLookup {
  promo_code: string;
  status: PromoLookupState;
  thirty_day_promo: boolean;
  label: string | null;
  decision_reason: string | null;
  checked_at: string | null;
  checking_now: boolean;
  lookup_enabled: boolean;
}

export type RecheckResult =
  | "queued"
  | "too_soon"
  | "busy"
  | "checking"
  | "disabled"
  | "not_used"
  | "not_needed";

export interface RecheckResponse {
  result: RecheckResult;
  keys: number;
  retry_after_seconds?: number;
}

/** Same trim the database uses for a CTA's promo (mig 491 _cta_promo_inputs_v). */
export const normalizePromoCode = (code: string | null | undefined): string =>
  (code ?? "").trim();

/**
 * Characters that have broken a lookup before ("e&eguns" is unknown to USLS). Letters, digits, "-" and "_" are
 * the usual shape; anything else is worth a second look. A hint only.
 */
export const hasUnusualPromoCharacters = (code: string): boolean =>
  /[^A-Za-z0-9_-]/.test(normalizePromoCode(code));

export async function fetchPromoCodeStatus(
  code: string,
): Promise<PromoCodeLookup | null> {
  const c = normalizePromoCode(code);
  if (!c || c.length > 100) {
    return null;
  }
  const { data, error } = await supabase.rpc("cta_promo_code_status", {
    p_codes: [c],
  });
  if (error) {
    throw error;
  }
  const rows = (data ?? []) as PromoCodeLookup[];
  return rows[0] ?? null;
}

export async function recheckPromoCode(code: string): Promise<RecheckResponse> {
  const { data, error } = await supabase.rpc("cta_promo_code_recheck", {
    p_code: normalizePromoCode(code),
  });
  if (error) {
    throw error;
  }
  return data as RecheckResponse;
}

export type PromoTone = "ok" | "warn" | "muted";

/** Plain, short wording for one lookup row. Pure, so it is unit-tested state by state. */
export function describePromoLookup(row: PromoCodeLookup): {
  tone: PromoTone;
  text: string;
} {
  switch (row.status) {
    case "live": {
      if (row.decision_reason === "promo_inactive") {
        return {
          tone: "warn",
          text: "U.S. LawShield knows this code, but it is switched off there. Signups may not get the discount.",
        };
      }
      if (row.decision_reason === "promo_expired") {
        return {
          tone: "warn",
          text: "U.S. LawShield knows this code, but it has expired there. Signups may not get the discount.",
        };
      }
      const label = row.label?.trim();
      if (label) {
        return { tone: "ok", text: `Checked with U.S. LawShield: ${label}` };
      }
      return {
        tone: "ok",
        text: row.thirty_day_promo
          ? "Checked with U.S. LawShield: free month"
          : "Checked with U.S. LawShield: code recognised",
      };
    }
    case "not_found":
      return {
        tone: "warn",
        text: "U.S. LawShield doesn't recognise this code yet. Signups may not get the discount. Ask U.S. LawShield to set it up, or check the spelling.",
      };
    case "error":
      return { tone: "muted", text: "Couldn't check right now" };
    case "pending":
      return { tone: "muted", text: "Checking…" };
    case "unchecked":
    default:
      return {
        tone: "muted",
        text: "Not checked yet. It is checked once a CTA with this code is saved for a place with a state.",
      };
  }
}

export function describeRecheck(r: RecheckResponse): string {
  switch (r.result) {
    case "queued":
      return "Asked again. The answer usually arrives within a minute.";
    case "too_soon":
      return "Asked a moment ago. Try again in a minute.";
    case "busy":
      return "The checker is busy. Try again in a few minutes.";
    case "checking":
      return "Already checking.";
    case "disabled":
      return "Promo checks are switched off.";
    case "not_needed":
      return "Already confirmed. It is re-checked automatically every week.";
    case "not_used":
      return "Save the CTA first. The code is checked after saving.";
    default:
      return "";
  }
}
